import { normalizeRequirementType } from './requirementType';
import { normalizeExcelDate } from './excelTransfer';

export const REQUIREMENT_EXPORT_COLUMNS = [
  { key: 'regId', label: 'Reg Id' },
  { key: 'client', label: 'Client' },
  { key: 'role', label: 'Role' },
  { key: 'typeOfContract', label: 'Type Of Contract' },
  { key: 'requirementtype', label: 'Requirement Type' },
  { key: 'startDate', label: 'Start Date' },
  { key: 'duration', label: 'Duration' },
  { key: 'location', label: 'Location' },
  { key: 'sourceCtc', label: 'Source CTC' },
  { key: 'qualification', label: 'Qualification' },
  { key: 'yearsExperience', label: 'Years Experience' },
  { key: 'relevantExperience', label: 'Relevant Experience' },
  { key: 'skill', label: 'Skill' },
  { key: 'numberOfPositions', label: 'Number of Positions' },
  { key: 'workMode', label: 'Work Mode' },
];

export const REQUIREMENT_IMPORT_TEMPLATE_COLUMNS = REQUIREMENT_EXPORT_COLUMNS;

export const REQUIREMENT_IMPORT_EXAMPLE = {
  regId: '99001',
  client: 'ABC Corp',
  role: 'Java Developer',
  typeOfContract: 'FTE',
  requirementtype: 'High',
  startDate: '2026-07-20',
  duration: '12 months',
  location: 'Bangalore',
  sourceCtc: '8-10 LPA',
  qualification: 'B.Tech',
  yearsExperience: '3-5',
  relevantExperience: '2-3',
  skill: 'Java, Spring',
  numberOfPositions: '1',
  workMode: 'Hybrid',
};

const HEADER_ALIASES = {
  regId: ['reg id', 'regid', 'requirement id'],
  client: ['client', 'client name'],
  role: ['role'],
  typeOfContract: ['type of contract', 'contract type'],
  requirementtype: ['requirement type', 'req type'],
  startDate: ['start date'],
  duration: ['duration'],
  location: ['location'],
  sourceCtc: ['source ctc', 'ctc'],
  qualification: ['qualification'],
  yearsExperience: ['years experience', 'yoe'],
  relevantExperience: ['relevant experience'],
  skill: ['skill', 'skills'],
  numberOfPositions: ['number of positions', 'positions', 'openings'],
  workMode: ['work mode'],
};

function normalizeHeader(header) {
  return String(header || '').trim().toLowerCase();
}

function getRowValue(row, fieldKey) {
  const aliases = HEADER_ALIASES[fieldKey] || [fieldKey];
  const entries = Object.entries(row);
  for (const [header, value] of entries) {
    const normalized = normalizeHeader(header);
    if (aliases.includes(normalized) || normalized === fieldKey.toLowerCase()) {
      return value;
    }
  }
  return row[fieldKey] ?? '';
}

export function mapRequirementToExportRow(req, extras = {}) {
  const startDate = req.startDate ? new Date(req.startDate).toLocaleDateString('en-CA') : '';
  return {
    regId: req.regId || '',
    client: req.client || '',
    role: req.role || '',
    typeOfContract: req.typeOfContract || '',
    requirementtype: normalizeRequirementType(req.requirementtype) || '',
    startDate,
    duration: req.duration || '',
    location: req.location || '',
    sourceCtc: req.sourceCtc || '',
    qualification: req.qualification || '',
    yearsExperience: req.yearsExperience || '',
    relevantExperience: req.relevantExperience || '',
    skill: req.skill || '',
    numberOfPositions: req.numberOfPositions || 1,
    workMode: req.workMode || '',
    uploadedOn: req.uploadedDate ? new Date(req.uploadedDate).toLocaleDateString('en-CA') : '',
    profileCount: extras.profileCount ?? '',
    claimCount: extras.claimCount ?? '',
  };
}

export const REQUIREMENT_LIST_EXPORT_COLUMNS = [
  ...REQUIREMENT_EXPORT_COLUMNS,
  { key: 'uploadedOn', label: 'Uploaded On' },
  { key: 'profileCount', label: 'No of Profiles' },
  { key: 'claimCount', label: 'No of Claims' },
];

export function mapImportRowToPayload(row, uploadedBy) {
  const requirementtype = normalizeRequirementType(getRowValue(row, 'requirementtype')) || 'High';
  return {
    regId: String(getRowValue(row, 'regId') || '').trim(),
    client: String(getRowValue(row, 'client') || '').trim(),
    role: String(getRowValue(row, 'role') || '').trim(),
    typeOfContract: String(getRowValue(row, 'typeOfContract') || '').trim(),
    requirmentType: requirementtype,
    startDate: normalizeExcelDate(getRowValue(row, 'startDate')),
    duration: String(getRowValue(row, 'duration') || '—').trim(),
    location: String(getRowValue(row, 'location') || '—').trim(),
    sourceCtc: String(getRowValue(row, 'sourceCtc') || '—').trim(),
    qualification: String(getRowValue(row, 'qualification') || '—').trim(),
    yearsExperience: String(getRowValue(row, 'yearsExperience') || '—').trim(),
    relevantExperience: String(getRowValue(row, 'relevantExperience') || '—').trim(),
    skill: String(getRowValue(row, 'skill') || '—').trim(),
    numberOfPositions: Number(getRowValue(row, 'numberOfPositions')) || 1,
    workMode: String(getRowValue(row, 'workMode') || '').trim(),
    uploadedBy,
    update: 'New',
    assessments: [],
  };
}

export function buildImportPayloads(rows, uploadedBy) {
  const payloads = rows.map((row) => mapImportRowToPayload(row, uploadedBy));
  return payloads.filter((payload) => (
    payload.regId
    && payload.client
    && payload.typeOfContract
    && payload.startDate
    && payload.role
  ));
}

export function getRequirementImportValidationHint(rows = [], payloads = []) {
  if (!rows.length) return 'The file is empty. Use Download Template and fill required columns.';
  if (payloads.length) return '';

  const first = rows[0] || {};
  const headers = Object.keys(first).join(', ') || 'none';
  return `No valid rows found. Required: Reg Id, Client, Role, Type Of Contract, Start Date. Found columns: ${headers}`;
}
